import React, { memo, useCallback, useMemo, useRef, useState, FocusEvent, ChangeEvent, useEffect } from 'react'

import { Autocomplete, AutocompleteRenderInputParams, AutocompleteInputChangeReason } from '@mui/material'
import { useField, useFormikContext } from 'formik'
import Fuse from 'fuse.js'
import { useDeepCompareMemo } from 'use-deep-compare'

import { FormHelpersProviderReturn, InsuranceCompany, InsuranceFieldProps, FieldSettings } from '../../../ana-toolbox'
import { getHasError } from '../../utils/helpers/getHasError'
import { useFormHelpers } from '../../utils/hooks/useFormHelpers'
import { useSubmitCount } from '../../utils/hooks/useSubmitCount'
import { TextField } from '../../utils/shared/TextField'
import { TextFieldLabel } from '../../utils/shared/TextFieldLabel'
import { combinedFields } from '../../utils/yup/main'
import { FieldHelperText } from '../field-helper-text/field-helper-text'
import { Translate } from '../translate/translate'

import { insuranceFieldHelpers } from './insurance-field-helpers'
import * as S from './Insurance-field.styles'

const InsuranceFieldBase = memo(
  ({
    companiesOptions: options = [],
    label,
    value: externalValue,
    onChange,
    name = 'insurance',
    formik,
    helperText,
    inputValue,
    isRequired,
    error,
  }: InsuranceFieldProps) => {
    const initialInputValue = useRef(externalValue)
    const [isOpen, setIsOpen] = useState(false)

    const fuse = useMemo(() => {
      return new Fuse(options, {
        keys: ['insurerName'],
        threshold: 0.5,
        ignoreLocation: true,
        includeScore: true,
      })
    }, [options])

    const handleChange = (
      _: ChangeEvent<unknown>,
      option: string | Pick<InsuranceCompany, 'referenceId' | 'insurerName'>
    ) => {
      if (typeof option !== 'string') {
        onChange?.({
          referenceId: option.referenceId,
          value: option.insurerName,
        })
      }
    }

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      const iv = event.target.value

      /* Find an option where the `insurerName` matches the input value. It's not case sensitive */
      const optionForInputValue = options.find((option) => option.insurerName.toLowerCase() === iv.toLowerCase())

      if (optionForInputValue) {
        onChange?.({
          referenceId: optionForInputValue?.referenceId,
          value: optionForInputValue?.insurerName,
        })
      }
    }

    const handleInputChange = (_: React.ChangeEvent<unknown>, value: string, reason: AutocompleteInputChangeReason) => {
      if (reason === 'input') {
        onChange?.({
          referenceId: undefined,
          value,
        })
      }
    }

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
      formik?.handleFocus?.(e)

      setIsOpen(true)
    }

    const handleClose = () => {
      setIsOpen(false)
    }

    const renderInput = (params: AutocompleteRenderInputParams) => {
      return (
        <>
          <TextField
            {...params}
            name={name}
            error={Boolean(error)}
            type="search"
            label={
              <TextFieldLabel required={typeof isRequired === 'function' ? false : isRequired}>
                <Translate>{label}</Translate>
              </TextFieldLabel>
            }
            InputLabelProps={{
              id: `${name}-label`,
            }}
            InputProps={{
              ...params.InputProps,
              className: undefined,
              style: {
                padding: '22px 16px 3px',
              },
            }}
            inputProps={{
              ...params.inputProps,
              value: (externalValue ? selectedOption?.insurerName : inputValue) || '',
              id: `${name}-input`,
              'aria-labelledby': `${name}-label`,
              autoComplete: 'off',
              style: {
                padding: 0,
              },
            }}
            fullWidth
          />

          <FieldHelperText name={name} helperText={helperText} error={error} />
        </>
      )
    }

    const getOptionLabel = (option: Pick<InsuranceCompany, 'insurerName'> | string) => {
      return typeof option === 'string' ? option : option.insurerName
    }

    const isSelected = (option: Pick<InsuranceCompany, 'referenceId'>) => option.referenceId === externalValue
    const selectedOption = options.find(isSelected)
    const hasInitialValue = typeof initialInputValue.current === 'string'

    const filterOptions = (
      o: Pick<InsuranceCompany, 'referenceId' | 'insurerName'>[],
      { inputValue: value }: { inputValue: string | undefined }
    ) => {
      if (!value) return o

      const results = insuranceFieldHelpers.getSuggestions(value, {
        fuse,
        getOptionKey: (suggestion) => suggestion.referenceId,
      })

      return results
    }

    const sortedOptions = useMemo(() => {
      return [...options].sort((a, b) =>
        getOptionLabel(a)?.toLowerCase().localeCompare(getOptionLabel(b)?.toLowerCase())
      )
    }, [options])

    return (
      <S.Container>
        <Autocomplete
          open={isOpen}
          options={sortedOptions}
          value={(hasInitialValue ? externalValue : undefined) || ''}
          inputValue={(externalValue ? selectedOption?.insurerName : inputValue) || ''}
          getOptionLabel={getOptionLabel}
          renderInput={renderInput}
          filterOptions={filterOptions}
          onChange={handleChange}
          onBlur={handleBlur}
          onClose={handleClose}
          onFocus={handleFocus}
          onInputChange={handleInputChange}
          autoHighlight
          disableClearable
          disablePortal
          freeSolo
        />
      </S.Container>
    )
  }
)

InsuranceFieldBase.displayName = 'InsuranceFieldBase'

export const InsuranceField = (props: InsuranceFieldProps) => {
  const submitCount = useSubmitCount()
  const formHelpers = useFormHelpers()
  const { validateForm } = useFormikContext()
  const [field, meta, helpers] = useField(props.name)
  const [idField, idMeta, idHelpers] = useField(`${props.name}Id`)

  const handleChange = useCallback(
    ({ referenceId, value }: { referenceId?: string; value: string }) => {
      fieldSettings?.hooks?.beforeChange?.(referenceId || value, formHelpers as FormHelpersProviderReturn)

      idHelpers.setValue(referenceId)
      helpers.setValue(value)

      props.onChange?.({ value, referenceId })
    },
    // Ignoring these dependencies to prevent unnecessary re renderes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    if (idMeta.value) {
      handleChange({
        referenceId: idMeta.value,
        value: props.companiesOptions?.find((o) => o.referenceId === idMeta.value)?.insurerName || '',
      })
      validateForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hasError = getHasError(meta, submitCount ?? 1)

  const memoizedField = useDeepCompareMemo(() => field, [field])

  const fieldSettings = useMemo(
    () => combinedFields[props.name as keyof typeof combinedFields] as FieldSettings,
    [props.name]
  )

  return (
    <InsuranceFieldBase
      error={hasError ? meta.error : undefined}
      {...props}
      {...memoizedField}
      value={idField.value}
      inputValue={field.value || ''}
      onChange={handleChange}
    />
  )
}
